// RE-ISSUE DOCUMENT - decision form in the shell record-drawer anatomy (eyebrow + expand/close, title, status rail, label/value block, hint, Close-left footer, ONE primary).
// E4: POST /sales-documents/:kind/:id/reissue {reason} + Idempotency-Key (ONE key per open form), admin. A NEW document for the same money event, numbered by today's policy;
// the cancelled one keeps its number; no money moves. The new number is shown from the server answer. A 404 reads as "not available yet".
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { Emo } from '../../../shell/EmojiIcons';
import DrawerFrame from '../../../shell/drawer/DrawerFrame';
import DrawerHeader from '../../../shell/drawer/DrawerHeader';
import DrawerFooter from '../../../shell/drawer/DrawerFooter';
import { keyFor } from '../../../lib/idempotencyKey.mjs';
import { reissueRefusal, reissueWhy, reissuedNo } from './documentsModel.mjs';

const MAX = 500;
const Kv = ({ label, children }) => <div><div className="lab">{label}</div><div className="val">{children}</div></div>;

// doc = { kind, id, number, cancelled, customer }; onDone(data) after the drawer closes on a success (the caller reloads)
export default function ReissueDocumentDrawer({ doc, isAdmin, onClose, onDone }) {
  const { t, i18n } = useTranslation('documentsPage');
  const [wide, setWide] = useState(false);
  const keyRef = useRef(null);   // same reason = same key on a retry; an edited reason = a new key
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false); const [ref, setRef] = useState(null); const [done, setDone] = useState(null);
  const gate = reissueWhy(doc, isAdmin);
  const off = gate ? t(`why.${gate}`) : !reason.trim() ? t('reissue.reason_required') : reason.trim().length > MAX ? t('reissue.reason_tooLong', { max: MAX }) : '';
  const go = async () => {
    if (off || busy || done) return;
    setBusy(true); setRef(null);
    try {
      const res = await api.reissueSalesDocument(doc.kind, doc.id, reason.trim(), keyFor(keyRef, reason.trim()));
      if (res && res.ok) setDone(res.data || {}); else setRef(reissueRefusal(res, i18n.language));
    } catch { setRef({ code: null, text: '', unavailable: false }); } finally { setBusy(false); }   // no raw e.message: the generic sentence
  };
  const close = () => (done && onDone ? onDone(done) : onClose());
  const title = doc.number || t('draftNo'); const kindName = t(`kind.${doc.kind}`, { defaultValue: doc.kind });
  const refText = ref && (ref.text || t(`reissue.err.${ref.code}`, { defaultValue: t('reissue.err.generic') }));
  const newNo = done ? reissuedNo(done) : '';
  return (
    <div className="disp-pg">
      <DrawerFrame size={wide ? 'expanded' : 'standard'} onClose={close} label={title} testId="rd-drawer">
        <DrawerHeader eyebrow={t('reissue.eyebrow', { kind: kindName }).toUpperCase()} ini="R" title={<span className="mono">{title}</span>} size={wide ? 'expanded' : 'standard'} onSize={() => setWide((w) => !w)} onClose={close}>
          {doc.customer && <div className="dr-meta">{doc.customer}</div>}
          <div className="rail"><span className="st done">{t('status.cancelled')}</span><span className="sep" /><span className={`st${done ? ' now' : ''}`}>{t('reissue.newDoc')}</span></div>
        </DrawerHeader>
        <div className="dr-body">
          {ref && <div className="errbox" role="alert" data-testid="rd-refusal">{ref.code && <span className="mono">{ref.code} </span>}{refText}</div>}
          {done && <div className="okbox" role="status" data-testid="rd-done" style={{ marginBottom: 12 }}><Emo name="check" /> {t('reissue.done')} <b className="mono" data-testid="rd-new-no">{newNo || '—'}</b></div>}
          <div className="kv" data-testid="rd-kv">
            <Kv label={t('cancel.type')}>{kindName}</Kv>
            <Kv label={t('reissue.cancelledNo')}><span className="mono">{doc.number || '—'}</span></Kv>
            <Kv label={t('col.customer')}>{doc.customer || '—'}</Kv>
          </div>
          <label className="lab" htmlFor="rd-reason" style={{ display: 'block', marginTop: 12 }}>{t('reissue.reason')}</label>
          <textarea id="rd-reason" className="inp" rows={3} maxLength={MAX} style={{ width: '100%' }} value={reason} disabled={!!gate || !!done} onChange={(e) => setReason(e.target.value)} placeholder={t('reissue.reasonPh')} data-testid="rd-reason" />
          {!gate && !done && !reason.trim() && <div className="sub" data-testid="rd-reason-hint" role="status"><Emo name="info" /> {t('reissue.reason_required')}</div>}
          {gate && <div className="sub" data-testid="rd-gate" role="status"><Emo name="lock" /> {t(`why.${gate}`)}</div>}
          <div className="hint" data-testid="rd-effect"><Emo name="info" /> <span>{t('reissue.effect')}</span></div>
        </div>
        <DrawerFooter onClose={close}
          primary={<button type="button" className="btn success" disabled={busy || !!off || !!done} aria-disabled={!!off || !!done} title={off || (done ? t('reissue.alreadyDone') : undefined)} onClick={go} data-testid="rd-confirm">{busy ? t('reissue.busy') : t('reissue.confirm')}</button>} />
      </DrawerFrame>
    </div>
  );
}
